<template>
  <div>
    <h1>Port Specific Trade Flow Monitoring </h1>
    <b-row>
      <b-col cols="12">
        <filters @search="applyFilter"
                 @berths-loaded="handleBerthsLoaded"
                 :selected-berths="selectedBerths"/>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="6">
        <segments-chart
          class="port-card"
          title="Top Commodities"
          :show-percentage="true"
          :items="commodityStat" />
      </b-col>
      <b-col cols="6">
        <directions-stat
          class="port-card"
          :title="this.apiFilterValues.direction === 'export' ? 'Top Destinations' : 'Top Origins'"
          :countries="destinationsStat.countries"
          :ports="destinationsStat.ports"/>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="6">
        <movements-stat class="port-card" :stats="movementsStat"/>
      </b-col>
      <b-col lg="6">
        <b-card no-body class="port-card">
          <template #header>Map</template>
          <b-card-body body-class="p-0">

            <port-tradeflows-map
              :berths="berths"
              @berth-updated="handleBerthUpdated" />

          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <periodic-stats
          :title="this.apiFilterValues.direction === 'export' ? 'Volume of Exports' : 'Volume of Imports'"
          class="port-card"
          yAxisLabel="Volume (metric tons)"
          :seasonal="true"
          :grouped="true"
          :stacked="true"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicVolumeStats"
          @search="(parameters) => {
            applyFilter({
              _periodicVolumeRange: parameters.range,
              _periodicVolumeGroup: parameters.group,
            })
          }"
          :apiRequestIsPerforming="!!getActiveRequestsCount"/>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <periodic-stats
          title="Voyage count"
          class="port-card"
          yAxisLabel="# of voyages"
          values-format="float1"
          :seasonal="true"
          :grouped="true"
          :stacked="true"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicVoyagesStats"
          @search="(parameters) => {
            applyFilter({
              _periodicVoyagesRange: parameters.range,
              _periodicVoyagesGroup: parameters.group,
            })
          }"
          :apiRequestIsPerforming="!!getActiveRequestsCount"/>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <periodic-stats
          title="Average time waiting at anchor"
          class="port-card"
          yAxisLabel="Average time at anchor (days)"
          values-format="float1"
          :seasonal="true"
          :grouped="true"
          :stacked="false"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicAvgTimeAnchorageStats"
          @search="(parameters) => {
            applyFilter({
              _periodicAvgTimeAnchorageRange: parameters.range,
              _periodicAvgTimeAnchorageGroup: parameters.group,
            })
          }"
          :apiRequestIsPerforming="!!getActiveRequestsCount"/>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <periodic-stats
          title="Average time in berth"
          class="port-card"
          yAxisLabel="Average time in berth (days)"
          values-format="float1"
          :seasonal="true"
          :grouped="true"
          :stacked="false"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicAvgTimeBerthStats"
          @search="(parameters) => {
            applyFilter({
              _periodicAvgTimeBerthRange: parameters.range,
              _periodicAvgTimeBerthGroup: parameters.group,
            })
          }"
          :apiRequestIsPerforming="!!getActiveRequestsCount"/>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="12" class="d-flex">
        <voyages
          :disabled="!filtersReady"
          :items="voyages.items"
          :sorting="allVoyagesLoaded"
          :currentPage="voyages.currentPage"
          :export-types="['csv']"
          :export-loading="exportLoading.voyages"
          @export="handleVoyagesExport"
          @page-changed="voyagesPageChanged"/>

      </b-col>
    </b-row>

  </div>
</template>

<script>
import getNested from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import Filters from '../components/porttradeflows/Filters';
import SegmentsChart from '../components/widgets/SegmentsChart';
import {mapActions, mapGetters} from 'vuex';
import DirectionsStat from '../components/tradeflows/DirectionsStat';
import MovementsStat from '../components/tradeflows/MovementsStat';
import PeriodicStats from '../components/tradeflows/PeriodicStats';
import PortTradeflowsMap from '../components/porttradeflows/PortTradeflowsMap';
import Voyages from '../components/tradeflows/Voyages';
import moment from 'moment';
import { downloadAsFileByURL } from '@/shared/download-utils';


export default {
  name: 'port-tradeflows',

  components: {
    Voyages,
    PortTradeflowsMap,
    PeriodicStats,
    MovementsStat,
    DirectionsStat,
    SegmentsChart,
    Filters,
  },

  data() {
    return {
      commodityStat: [],
      destinationsStat: {
        countries: [],
        ports: [],
      },
      movementsStat: {},
      periodicVolumeStats: [],
      periodicVoyagesStats: [],
      periodicAvgTimeBerthStats: [],
      periodicAvgTimeAnchorageStats: [],


      initialLoadRequestsCount: 10, // requests amount when filter is changed
      preloadRequestsCount: 1, // requests amount which will be performed on load
      preloadPagesRange: 5, // if (totalPages - currentPage) < preloadPagesRange
                            // load another "preloadRequestsCount" requests
      voyages: {
        items: [],
        apiItemsPerPage: 50,  // hardcoded for now (can be calculated by 2 requests to api and comparing items count)
        apiTotalPages: null,  // or maxPage, it doesn't matter since pages are starts from "1"
        apiCurrentPage: null,
        currentPage: null,
      },

      rangeChoices: [
        {text: 'Weekly', value: 'weekly'},
        {text: 'Monthly', value: 'month'},
        {text: 'Quarterly', value: 'quarter'},
        {text: 'Annual', value: 'year'},
      ],

      apiFilterValues: {
        _periodicVolumeRange: 'weekly',
        _periodicVolumeGroup: null,
        _periodicVoyagesRange: 'weekly',
        _periodicVoyagesGroup: null,
        _periodicAvgTimeBerthRange: 'weekly',
        _periodicAvgTimeBerthGroup: null,
        _periodicAvgTimeAnchorageRange: 'weekly',
        _periodicAvgTimeAnchorageGroup: null,
      },

      berths: [],
      selectedBerths: [],

      exportLoading: {
        voyages: false,
      },
    }
  },

  methods: {
    ...mapActions(
      'api/tradeflows', [
        'getCountriesImportStat',
        'getPortsImportStat',
        'getCountriesExportStat',
        'getPortsExportStat',
        'getMovementsStat',
        'getVoyagesPeriodicStat',
        'getCommodityStat',
        'getVoyages',
      ]
    ),

    async applyFilter(apiFilterValues) {
      window.analytics.track('PortFlowSearch',this.filterParameters);
      this.apiFilterValues = {...this.apiFilterValues, ...apiFilterValues};
      this.selectedBerths = this.apiFilterValues.berthId;

      if(this.filtersReady) {
        let rangeFieldName = this.getRangeFieldName(apiFilterValues)
        if(rangeFieldName) {
          // 3 variants possible here: volume, voyages, avgtime
          await this.loadPeriodicStats(apiFilterValues);
        } else {
          // we don't need "range" fields in these sections
          let parameters = this.getBaseFilterParameters();
          await this.loadCommodityStat(parameters);

          if(apiFilterValues.direction === 'export') {
            await this.loadTopDestinations(parameters);
          } else {
            await this.loadTopOrigins(parameters);
          }
          await this.loadMovementsStat(parameters);
          // load all periodic stats
          await this.loadPeriodicStats(apiFilterValues);
          await this.loadVoyages(parameters);
        }
      } else {
        this.resetDashboardData();
      }
    },

    handleBerthUpdated(berth) {
      if(berth.selected && !this.selectedBerths.includes(berth.value)) {
        this.selectedBerths.push(berth.value);
      }
      if(!berth.selected && this.selectedBerths.includes(berth.value)) {
        this.selectedBerths = this.selectedBerths.filter(berthId => berthId !== berth.value);
      }
    },

    handleBerthsLoaded(berths) {
      this.berths = berths;
    },

    async loadCommodityStat(filterParameters) {
      this.commodityStat = await this.getCommodityStat(filterParameters);
    },

    async loadTopDestinations(filterParameters) {
      this.destinationsStat.countries = await this.getCountriesImportStat(filterParameters);
      this.destinationsStat.ports = await this.getPortsImportStat(filterParameters);
    },

    async loadTopOrigins(filterParameters) {
      this.destinationsStat.countries = await this.getCountriesExportStat(filterParameters);
      this.destinationsStat.ports = await this.getPortsExportStat(filterParameters);
    },

    async loadMovementsStat(filterParameters) {
      this.movementsStat = await this.getMovementsStat(filterParameters) || [];
    },

    async loadPeriodicStats(filterParameters) {
      const methodsMap = {
        _periodicVolumeRange: this.loadPeriodicVolumeStats,
        _periodicVolumeGroup: this.loadPeriodicVolumeStats,

        _periodicVoyagesRange: this.loadPeriodicVoyagesStats,
        _periodicVoyagesGroup: this.loadPeriodicVoyagesStats,

        _periodicAvgTimeBerthRange: this.loadPeriodicAvgTimeBerthStats,
        _periodicAvgTimeBerthGroup: this.loadPeriodicAvgTimeBerthStats,

        _periodicAvgTimeAnchorageRange: this.loadPeriodicAvgTimeAnchorageStats,
        _periodicAvgTimeAnchorageGroup: this.loadPeriodicAvgTimeAnchorageStats,
      };
      let parameters = null;
      let rangeFieldName = this.getRangeFieldName(filterParameters);

      if(rangeFieldName) {
        // load only specific section
        parameters = this.getBaseFilterParameters();
        await methodsMap[rangeFieldName](parameters);
      } else {
        // load all sections
        for(let method of new Set(Object.values(methodsMap))) {
          parameters = this.getBaseFilterParameters();
          await method(parameters);
        }
      }
    },

    async handleExportVoyages(format) {
      window.analytics.track('TradeflowsVoyagesExport',this.filterParameters);
      const limit = 5000;
      let response = await this.getVoyages({
        ...this.filterParameters,
        format,
        limit: limit
      });
      this.csvModal.link = response.data.csv;
      this.csvModal.alertText = `CSV download for voyages is currently limited to ${limit} rows per download`;
      this.csvModal.show = true;
      // const timestamp = moment().format('YYYY-MM-DD-HH-mm');
      // downloadAsFile(response, `tradeflow_timeseries_export_${timestamp}.${format}`);
    },

    getRangeFieldName(filterParameters) {
      return Object.keys(filterParameters).filter(
        fieldName => fieldName.startsWith('_periodic')
      )[0];
    },

    getBaseFilterParameters() {
      let parameters = {};
      Object.keys(this.apiFilterValues).forEach(fieldName => {
        if(!fieldName.startsWith('_')) {
          parameters[fieldName] = this.apiFilterValues[fieldName];
        }
      });

      if(parameters.direction === 'export') {
        parameters.loadPortId = parameters.portId;
        parameters.loadBerthId = parameters.berthId;
      } else if(parameters.direction === 'import') {
        parameters.dischargePortId = parameters.portId;
        parameters.dischargeBerthId = parameters.berthId;
        parameters.flowDate = "import";
      }
      delete parameters.portId;
      delete parameters.berthId;
      delete parameters.direction;

      return parameters;
    },

    async loadPeriodicVolumeStats(filterParameters) {
      let parameters = {
        ...filterParameters,
        range: this.apiFilterValues._periodicVolumeRange,
        group: this.apiFilterValues._periodicVolumeGroup,
        metric: null,
      };

      let response = await this.getVoyagesPeriodicStat(parameters) || {};
      this.periodicVolumeStats = response.data || [];
    },

    async loadPeriodicVoyagesStats(filterParameters) {
      let parameters = {
        ...filterParameters,
        range: this.apiFilterValues._periodicVoyagesRange,
        group: this.apiFilterValues._periodicVoyagesGroup,
        metric: 'count',
      };

      let response = await this.getVoyagesPeriodicStat(parameters) || {};
      this.periodicVoyagesStats = response.data || [];
    },

    async loadPeriodicAvgTimeBerthStats(filterParameters) {
      let parameters = {
        ...filterParameters,
        range: this.apiFilterValues._periodicAvgTimeBerthRange,
        group: this.apiFilterValues._periodicAvgTimeBerthGroup,
        metric: `average_${this.directionOperation}_days_in_berth`,
      };

      let response = await this.getVoyagesPeriodicStat(parameters) || {};
      this.periodicAvgTimeBerthStats = response.data || [];
    },

    async loadPeriodicAvgTimeAnchorageStats(filterParameters) {
      let parameters = {
        ...filterParameters,
        range: this.apiFilterValues._periodicAvgTimeAnchorageRange,
        group: this.apiFilterValues._periodicAvgTimeAnchorageGroup,
        metric: `average_${this.directionOperation}_days_in_anchorage`,
      };

      let response = await this.getVoyagesPeriodicStat(parameters) || {};
      this.periodicAvgTimeAnchorageStats = response.data || [];
    },

    async loadVoyages(filterParameters) {
      // it's possible to have a lot of pages here
      // they are loaded async to prevent interface lags

      this.voyages.items = [];
      this.voyages.currentPage = 1;
      await this.fetchVoyages({...filterParameters, page: 1}, this.initialLoadRequestsCount);
    },

    async voyagesPageChanged(currentPage, totalPages) {
      // voyages on demand loading, overview:
      // 1. detect when a current page is less than "preloadPagesRange" value
      // 2. add items which are received by "preloadRequestsCount" requests

      if(this.getActiveRequestsCount) {
        return;  // prevent multiple requests
      }

      // calculate an API page number to start
      let apiPagesLoaded = this.voyages.items.length / this.voyages.apiItemsPerPage;

      if((totalPages - currentPage) < this.preloadPagesRange && Number.isInteger(apiPagesLoaded)) {
        let parameters = this.getBaseFilterParameters();
        await this.fetchVoyages(
          {...parameters, page: apiPagesLoaded + 1},
          this.preloadRequestsCount
        );
        // b-pagination resets a current page when data is changed we have to restore it this way
        // https://github.com/bootstrap-vue/bootstrap-vue/issues/3372
        this.voyages.currentPage = currentPage;
      }
    },

    async fetchVoyages(filterParameters, requestsCount) {
      let countdown = requestsCount;  // no more than countdown requests
      let parameters = {...filterParameters};
      let response = null;

      do {
        response = await this.getVoyages(parameters) || {};

        if(response.data && response.data.data) {
          this.voyages.items.push.apply(this.voyages.items, response.data.data);
          this.voyages.apiTotalPages = response.data.maxPage;
          this.voyages.apiCurrentPage = response.data.currentPage;
          parameters.page += 1;
        } else {
          break;
        }
        countdown--;
      } while (parameters.page <= response.data.maxPage && countdown);
    },

    async handleVoyagesExport(format) {
      window.analytics.track('TradeflowsVoyagesExport',this.filterParameters);
      this.exportLoading.voyages = true;
      const parameters = {
        format,
        limit: 1000000,
        ...this.getBaseFilterParameters()
      };
      const response = await this.getVoyages(parameters);

      const timestamp = moment().format('YYYY-MM-DD-HH-mm');
      downloadAsFileByURL(
        getNested(response, 'data.csv', null),
        `port-comparison-voyages-export_${timestamp}.${format}`
      );
      this.exportLoading.voyages = false;
    },

    resetDashboardData() {
      this.commodityStat = [];
      this.destinationsStat = {countries: [], ports: []};
      this.movementsStat = {};
      this.periodicVolumeStats = [];
      this.periodicVoyagesStats = [];
      this.periodicAvgTimeStats = [];

      this.voyages.items = [];
      this.voyages.apiTotalPages = null;
      this.voyages.currentPage = null;
    },
  },

  computed: {
    ...mapGetters(
      'api', [
        'getActiveRequestsCount',
      ]
    ),

    filtersReady() {
      return !isEmpty(this.apiFilterValues.berthId);
    },

    allVoyagesLoaded() {
      return (
        this.voyages.apiTotalPages &&
        this.voyages.apiCurrentPage &&
        this.voyages.apiTotalPages === this.voyages.apiCurrentPage
      );
    },

    directionOperation() {
      if (this.apiFilterValues.direction === 'export') {
        return 'load';
      } else if (this.apiFilterValues.direction === 'import') {
        return 'discharge';
      }
      return '-unknown-operation-';
    },
  },
}
</script>

<style scoped>
.port-card >>> .card-body {
  /* deep-scoped style, affects child components */
  height: 370px;
}

/*.port-card >>> .chart-container {*/
/*  height: 420px;*/
/*}*/
</style>
